import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureBoundaryError } from "@/lib/sentry";
import { isAssetLoadError, reloadForAssetError } from "@/lib/asset-reload";

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
  /** An asset failure we expect to heal by reloading, so it is not shown or reported. */
  recovering: boolean;
};

export class AppErrorBoundary extends Component<Props, State> {
  state: State = {
    error: null,
    recovering: false,
  };

  static getDerivedStateFromError(error: Error): State {
    // Assume an asset failure is recoverable until componentDidCatch finds out
    // whether a reload was actually available; that keeps the failure screen from
    // flashing red in the moments before the page navigates.
    return { error, recovering: isAssetLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Stale deployment: the JS chunk or stylesheet this render needed no longer
    // exists on the server (a new deploy replaced the hashes). Reloading fetches
    // the new index.html and fresh chunks, so there is nothing to show and
    // nothing worth reporting — this is the "App render failed" noise that made
    // the failure look unhandled in Sentry.
    if (isAssetLoadError(error) && reloadForAssetError()) return;

    // Not recoverable: either an ordinary bug, or the reload budget is spent
    // because the asset is genuinely gone rather than merely renamed.
    this.setState({ recovering: false });
    console.error("App render failed", error, errorInfo);
    captureBoundaryError(error, errorInfo);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    if (this.state.recovering) {
      return <div className="min-h-screen bg-slate-50" />;
    }

    return (
      <div className="min-h-screen bg-slate-50 text-slate-950 p-6 md:p-10">
        <div className="mx-auto max-w-3xl rounded-xl border border-red-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-red-700">La aplicación falló al renderizar</h1>
          <p className="mt-2 text-sm text-slate-600">
            Ocurrió un error inesperado. Podés intentar recargar la página o volver al inicio.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              onClick={() => { this.setState({ error: null }); window.location.href = "/"; }}
            >
              Volver al inicio
            </button>
            <button
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => window.location.reload()}
            >
              Recargar página
            </button>
          </div>
          {import.meta.env.DEV && (
            <pre className="mt-4 overflow-x-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100">
              {this.state.error.stack || this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}